/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  Card,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getDiasFestivosPorAñoPag,
  getDiasFestivosPorAño,
  postDiaFestivoPorAño,
  deleteDiaFestivoPorAño,
  patchDiaFestivoPorAño,
  getDiaFestivoPorAño,
  autorizarDiaFestivoPorAño,
} from 'api/configuraciones/clock/DiasFestivos/FestivosPorAño';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { getPeriodosFiscales } from 'api/configuraciones/periodosFiscales';
import { getModelPermission } from 'utils/permission';
import DiasFestivosDelAñoReloj from './DiasFestivosDelAñoReloj';

const DiasFestivosPorAñoReloj = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [disableDayTable, setdisableDayTable] = useState(false);
  const [años, setAños] = useState([]);
  const [pageActual, setPageActual] = useState(1);

  const AñosSinRepetir = (dataArray, excludeArray) => {
    // collect all "periodo" values from excludeArray into a Set for fast lookup
    const excludeSet = new Set(excludeArray.map((item) => item.periodo_fiscal.periodo));

    const result = dataArray.filter((item) => !excludeSet.has(item.periodo));
    // return only objects whose "año" is not in that Set
    if (selectedData?.periodo_fiscal) {
      result.push(
        selectedData?.periodo_fiscal,
      );
    }
    return result;
  };
  function safeNumber(value) {
    return (value === undefined || Number.isNaN(value)) ? 0 : value;
  }

  const formInstances = [
    form,
  ];

  const fetchPaginated = async (page = 1, q = null) => {
    try {
      setLoading(true);
      const cleanPage = Number(String(page).replace(/\D/g, '')); // solo dígitos
      const _data = await getDiasFestivosPorAñoPag({
        page: cleanPage,
        ...q,
      });

      const { results } = _data;

      setSelectedRowKeys([]);
      setPageActual(cleanPage);
      setData({ ..._data, results });
      setLoading(false);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    const _data = await getDiasFestivosPorAñoPag({ page, ...q });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
        _años,
      ] = await Promise.all([
        getEstadosGlobales(),
        getDiasFestivosPorAño(),
        getPeriodosFiscales({ estados_globales: ESTATUS_AUTORIZADO }),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(_data);
        setAños(_años);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setdisableDayTable(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data?.results?.find((item) => item.id === key);
    const _diasFestivos = await getDiaFestivoPorAño(match.id);
    const formValues = {
      // Formatea la información recibida (según lógica definida en formatReceived)
      ...formatReceived(_diasFestivos),
      periodo_fiscal: _diasFestivos.periodo_fiscal?.id,
      // Convierte la fecha de vigencia final a objeto moment si existe, si no null
      estados_globales: _diasFestivos.estados_globales?.id, //  aquí sí es un ID
    };
    setTimeout(() => {
      if (_diasFestivos) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(formValues));
          setDisableFields(_diasFestivos?.estados_globales?.id !== ESTATUS_ACTIVO);
          setdisableDayTable(_diasFestivos?.estados_globales?.id !== ESTATUS_ACTIVO);
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onClickAdd = () => {
    form.resetFields();
    onCancel();
    setVisible(true);
    setdisableDayTable(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      // Me rendi, desde arriba debe estar el id, no aqui, pero no tengo de otra.
      const saved = await (key ? patchDiaFestivoPorAño(key, values) : postDiaFestivoPorAño(values));
      if (saved) {
        if (_continue) {
          await fetchData(pageActual);
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData(pageActual);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteDiaFestivoPorAño(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData(pageActual);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un Día Festivo ya autorizado');
        return;
      }
      const saved = await autorizarDiaFestivoPorAño(key);
      if (saved) {
        const _data = await getDiasFestivosPorAño();
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Año',
      dataIndex: 'periodo_fiscal',
      key: 'periodo_fiscal',
      width: 100,
      render: (_, record) => record?.periodo_fiscal?.periodo || 'n/a',
    },
    {
      titleText: 'Fundamento',
      dataIndex: 'fundamento',
      key: 'fundamento',
      width: 400,
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 400,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (_, record) => record?.estados_globales?.descripcion || 'n/a',
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            cols={columns}
            data={data?.results || []}
            pagination={false}
            paginationCallback={fetchPaginated}
            count={data?.count || 0}
            rowSelection={rowSelection}
            currentPage={pageActual}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            customActions={[
              {
                onClick: () => setVisibleAutorizar(true),
                permission: { model: 'diafestivo', action: 'autorizar' },
                icon: <CheckCircleOutlined />,
                text: 'Autorizar',
                disabledBySelection: true,
                disabled: selectedData?.estados_globales?.id !== ESTATUS_ACTIVO,
                className: 'success',
              },
            ]}
            disableDelete={selectedData?.estados_globales?.id !== ESTATUS_ACTIVO}
            permission={permission}
            allowExportParam={false}
            allowSearch={false}
          />
        )
          : (
            <Card
              className="form-container"
              title={`${selectedRowKeys.length ? 'EDITAR' : 'AGREGAR'} Dias Festivos Por Año`}
              extra={(
                <FormSubmitControls
                  onFinish={onFinish}
                  onCancel={onCancel}
                  loading={loading}
                  onFinishText="Guardar y Continuar"
                />
              )}
              bordered={false}
            >

              <Form
                layout="vertical"
                form={form}
                onFinish={onFinish}
                scrollToFirstError
                name="general"
                initialValues={{
                  estados_globales: ESTATUS_ACTIVO,
                }}
              >
                <Row gutter={10}>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="periodo_fiscal"
                      rules={rules.required}
                      label="Año"
                    >
                      <Select
                        labelProp="periodo"
                        keyProp="id"
                        dataSource={AñosSinRepetir(años, data?.results)}
                        disabled={disableFields}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="fundamento"
                      rules={rules.required}
                      label="Fundamento"
                    >
                      <Input
                        allowClear
                        disabled={disableFields}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      name="estados_globales"
                      label="Estatus"
                      hasFeedback
                    >
                      <Select
                        labelProp="descripcion"
                        keyProp="id"
                        dataSource={estados}
                        activeInactive={selectedData?.estados_globales === ESTATUS_ACTIVO}
                        disabled={selectedRowKeys.length <= 0 || disableFields}
                        onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={24}>
                    <Form.Item
                      name="observaciones"
                      rules={rules.required}
                      label="Observaciones"
                    >
                      <Input
                        allowClear
                        disabled={disableFields}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Form>
              <DiasFestivosDelAñoReloj
                permission={getModelPermission('diafestivodetalle')}
                año={Number(selectedData?.periodo_fiscal?.periodo)}
                idDelAño={safeNumber(selectedRowKeys[0])}
                disableFieldsParent={disableDayTable}
              />
            </Card>

          )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Día Festivo ${selectedData?.periodo_fiscal?.periodo}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar el Día Festivo ${selectedData?.periodo_fiscal?.periodo}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar el Día Festivo ${selectedData?.periodo_fiscal?.periodo}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default DiasFestivosPorAñoReloj;
